import Phaser from 'phaser';
import { DESIGN, COLORS } from '@/config';
import { sfx } from '@/audio/Sfx';
import { CameraController } from '@/util/CameraController';
import { MAP, type MapNode } from '@/data/schema/worldmap';
import { WORLD_MAP, mapNode, mapRegion } from '@/data/worldMapRegistry';
import { getLevel } from '@/data/levelRegistry';
import { progress } from '@/game/Progress';
import { HeavenSetpiece } from '@/scenes/HeavenSetpiece';

/** Per-realm accent (medallion + sealed sigil tint) — matches the painted plate
 *  and the theme palettes ([[theme-mythic-pantheons]]). */
const REALM_TINT: Record<string, number> = {
  olympus: 0x9fb6d8,
  duat: 0xc98b3a,
  helheim: 0x8fb8d8,
  yomi: 0xc0503a,
  xibalba: 0x3a8c6a,
  heaven: 0xf1e6bc,
};

/** Tier ring colour by highest difficulty cleared (0 = unplayed/unlocked). The
 *  ring is the at-a-glance "how far you've proven yourself" readout. */
const TIER_RING: { color: number; glow: boolean }[] = [
  { color: 0x5a6a86, glow: false }, // 0 unplayed
  { color: 0xb3793b, glow: false }, // 1 Mortal — bronze
  { color: 0xc7ccd6, glow: false }, // 2 Champion — silver
  { color: 0xe0b04a, glow: false }, // 3 Demigod — gold
  { color: 0xffe08a, glow: true }, // 4 Ascendant — radiant
];

const NODE_R = 58; // medallion radius (map-space px)
const REVEAL_R = 320; // fog clear radius around a revealed node

interface NodeView {
  node: MapNode;
  unlocked: boolean;
  /** The ring graphics, scaled for the pulse on a "play me" frontier node. */
  ring: Phaser.GameObjects.Graphics;
  medallion: Phaser.GameObjects.GameObject;
  /** Whether this node should pulse (unlocked, built, not yet cleared). */
  pulse: boolean;
}

export class WorldMapScene extends Phaser.Scene {
  private fog!: Phaser.GameObjects.RenderTexture;
  private routes!: Phaser.GameObjects.Graphics;
  private nodeViews: NodeView[] = [];
  private panel!: Phaser.GameObjects.Container;
  private selectedTier = 1;
  private camCtl!: CameraController;
  /** A separate, unzoomed camera for the screen UI so the map's pan/zoom never
   *  moves or scales the header/panel (and their buttons stay hit-testable). */
  private uiCam!: Phaser.Cameras.Scene2D.Camera;
  /** Screen-space rect of the open panel — a tap inside it is for the panel's own
   *  buttons, not a map tap (so selecting a tier / Embark doesn't close it). */
  private panelBounds?: { x: number; y: number; w: number; h: number };

  // Floating-Heaven finale set-piece + its ascension cinematic.
  private heaven?: HeavenSetpiece;
  private heavenNode?: MapNode;
  private veil!: Phaser.GameObjects.Rectangle;
  private cinematic = false;

  constructor() {
    super('worldmap');
  }

  create(): void {
    this.nodeViews = [];
    this.cameras.main.setBackgroundColor('#0b0a14');

    this.drawBackdrop();
    this.makeFogBrush();
    this.buildFog();
    this.routes = this.add.graphics().setDepth(6);
    this.drawRevealedRoutes();
    this.buildNodes();
    this.buildHeaven();
    // Everything built so far is WORLD content (the atlas pans/zooms) — except
    // the screen-fixed ascension veil, which belongs with the UI.
    const worldObjects = this.children.list.filter((o) => o !== this.veil);
    this.buildUi();
    // New display objects since the snapshot are the UI (header, title, menu
    // chip, panel) — plus the veil pulled across above.
    const uiObjects = this.children.list.filter((o) => !worldObjects.includes(o));

    this.camCtl = new CameraController(this, {
      worldWidth: MAP.width,
      worldHeight: MAP.height,
      viewWidth: DESIGN.width,
      startAt: 'bottom', // the pilgrimage begins at Olympus, bottom of the atlas
      onTap: (x, y) => this.handleTap(x, y),
    });

    // The UI rides its own 1:1 camera (added last → drawn on top), immune to the
    // map camera's pan/zoom; each camera ignores the other's objects.
    this.uiCam = this.cameras.add(0, 0, DESIGN.width, DESIGN.height);
    this.cameras.main.ignore(uiObjects);
    this.uiCam.ignore(worldObjects);

    this.setupDevKeys();
    // If a stop was just unlocked by a victory, play its reveal once.
    this.playPendingReveal();
  }

  private fitZoom(): number {
    return DESIGN.width / MAP.width;
  }

  // ---- backdrop ------------------------------------------------------------

  private mapKey(): string | null {
    if (this.textures.exists('world_map')) return 'world_map';
    if (this.textures.exists('world_map_svg')) return 'world_map_svg';
    return null;
  }

  private drawBackdrop(): void {
    const key = this.mapKey();
    if (key) {
      this.add.image(0, 0, key).setOrigin(0, 0).setDisplaySize(MAP.width, MAP.height).setDepth(0);
    } else {
      const g = this.add.graphics().setDepth(0);
      g.fillGradientStyle(0xd3c293, 0xd3c293, 0xcdb98c, 0xcdb98c, 1);
      g.fillRect(0, 0, MAP.width, MAP.height);
    }
  }

  // ---- fog of war ----------------------------------------------------------

  /** A soft radial brush (white core → transparent rim) used to erase fog holes
   *  over explored realms. Built once into a texture. */
  private makeFogBrush(): void {
    if (this.textures.exists('fog_brush')) return;
    const R = REVEAL_R;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const steps = 40;
    for (let i = steps; i > 0; i--) {
      g.fillStyle(0xffffff, 0.05);
      g.fillCircle(R, R, (R * i) / steps);
    }
    g.generateTexture('fog_brush', R * 2, R * 2);
    g.destroy();
  }

  /** Dark fog over the whole atlas, with soft holes erased over every unlocked
   *  node so explored realms show the painted map and unexplored ones stay
   *  shrouded. The pending-reveal node is left fogged here — its hole lifts in
   *  the reveal animation. */
  private buildFog(): void {
    const pending = progress.peekPendingReveal();
    this.fog = this.add.renderTexture(0, 0, MAP.width, MAP.height).setOrigin(0, 0).setDepth(5);
    this.fog.fill(0x07060f, 0.72);
    for (const n of WORLD_MAP.nodes) {
      if (n.id === pending) continue;
      if (progress.isUnlocked(n.id)) this.eraseFogHole(n.x, n.y);
    }
  }

  private eraseFogHole(x: number, y: number): void {
    this.fog.erase('fog_brush', x - REVEAL_R, y - REVEAL_R);
  }

  // ---- routes (dotted treasure-map trail) ----------------------------------

  /** Points along a gently-bowed quadratic curve between two nodes, so the trail
   *  arcs like an inked route rather than a straight ruler line. */
  private routePoints(from: MapNode, to: MapNode): Phaser.Math.Vector2[] {
    const mid = new Phaser.Math.Vector2((from.x + to.x) / 2, (from.y + to.y) / 2);
    // Bow the control point perpendicular to the segment (~12% of its length).
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    const bow = len * 0.12;
    const ctrl = new Phaser.Math.Vector2(mid.x - (dy / len) * bow, mid.y + (dx / len) * bow);
    const curve = new Phaser.Curves.QuadraticBezier(
      new Phaser.Math.Vector2(from.x, from.y),
      ctrl,
      new Phaser.Math.Vector2(to.x, to.y),
    );
    // One dot per ~34px so spacing reads as a treasure-map dashed trail.
    const n = Math.max(4, Math.round(len / 34));
    return curve.getSpacedPoints(n);
  }

  /** Draw the dotted trail for every revealed leg (child node unlocked), minus
   *  the pending-reveal leg, which the animation draws progressively. */
  private drawRevealedRoutes(): void {
    const pending = progress.peekPendingReveal();
    this.routes.clear();
    for (const n of WORLD_MAP.nodes) {
      if (!n.prereq || n.id === pending) continue;
      if (!progress.isUnlocked(n.id)) continue;
      const from = mapNode(n.prereq);
      if (!from) continue;
      const pts = this.routePoints(from, n);
      this.drawDots(pts, pts.length);
    }
  }

  /** Render the first `count` dots of a route into the routes layer. */
  private drawDots(pts: Phaser.Math.Vector2[], count: number): void {
    const ink = 0x4a3a22;
    for (let i = 0; i < Math.min(count, pts.length); i++) {
      // Skip the dots buried under the node medallions at each end.
      const edge = i < 1 || i > pts.length - 2;
      this.routes.fillStyle(ink, edge ? 0.5 : 0.82);
      this.routes.fillCircle(pts[i].x, pts[i].y, 6);
    }
  }

  // ---- nodes ---------------------------------------------------------------

  private buildNodes(): void {
    for (const n of WORLD_MAP.nodes) this.nodeViews.push(this.buildNodeView(n));
  }

  private buildNodeView(node: MapNode): NodeView {
    // The Heaven finale is drawn by HeavenSetpiece, not a flat medallion — keep a
    // bare view purely so the node stays tappable (the tap plays the cinematic).
    if (mapRegion(node.regionId)?.realm === 'heaven') {
      return {
        node,
        unlocked: progress.isUnlocked(node.id),
        ring: this.add.graphics().setDepth(8),
        medallion: this.add.graphics().setDepth(8),
        pulse: false,
      };
    }
    const unlocked = progress.isUnlocked(node.id);
    const pending = progress.peekPendingReveal() === node.id;
    const built = !!(node.levelId && getLevel(node.levelId));
    const bestTier = node.levelId ? progress.getBestTier(node.levelId) : 0;
    const stars = node.levelId ? progress.getStars(node.levelId) : 0;

    const medallion = this.makeMedallion(node, unlocked);
    const ring = this.add.graphics().setDepth(8);
    this.drawRing(ring, node, unlocked, bestTier);

    // Star pips beneath the medallion, for cleared stops.
    if (unlocked && stars > 0) {
      this.add
        .text(node.x, node.y + NODE_R + 18, '★'.repeat(stars), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
          color: '#e0b04a',
        })
        .setOrigin(0.5)
        .setDepth(8);
    }

    // Name label beneath unlocked nodes (revealed stops are worth naming).
    if (unlocked) {
      this.add
        .text(node.x, node.y + NODE_R + (stars > 0 ? 44 : 20), node.name, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '21px',
          fontStyle: 'bold',
          color: COLORS.text,
          align: 'center',
        })
        .setOrigin(0.5)
        .setDepth(8)
        .setStroke('#07060f', 4);
    }

    const view: NodeView = {
      node,
      unlocked,
      ring,
      medallion,
      pulse: unlocked && built && bestTier === 0,
    };

    // The pending-reveal node starts hidden; the animation pops it in.
    if (pending) {
      ring.setAlpha(0);
      (medallion as unknown as Phaser.GameObjects.Components.Alpha).setAlpha(0);
    }
    return view;
  }

  /** The medallion: a circular crop of the realm's stage backdrop for built
   *  stops, else a realm-tinted disc. Locked stops render as a dark sealed
   *  sigil (a clasp), so the journey ahead reads as shrouded-but-visible. */
  private makeMedallion(node: MapNode, unlocked: boolean): Phaser.GameObjects.GameObject {
    const region = mapRegion(node.regionId);
    const tint = REALM_TINT[region?.realm ?? 'olympus'] ?? 0x9aa0d0;

    if (!unlocked) {
      // Sealed sigil — a dark clasp ring with a small lock, dimmed to read as
      // "not yet reached" under/over the fog.
      const g = this.add.graphics().setDepth(8).setAlpha(0.6);
      g.fillStyle(0x141320, 0.9).fillCircle(node.x, node.y, NODE_R);
      g.lineStyle(3, tint, 0.5).strokeCircle(node.x, node.y, NODE_R);
      const lock = this.add
        .text(node.x, node.y, '⚿', { fontFamily: 'system-ui, sans-serif', fontSize: '40px', color: '#7a7390' })
        .setOrigin(0.5)
        .setDepth(8)
        .setAlpha(0.7);
      // Group the lock with the disc so the reveal can fade them together.
      return this.add.container(0, 0, [g, lock]).setDepth(8);
    }

    const bgKey = this.stageBgKey(node);
    if (bgKey) {
      const img = this.add.image(node.x, node.y, bgKey).setDepth(8);
      const src = this.textures.get(bgKey).getSourceImage() as { width: number; height: number };
      const s = (2 * NODE_R) / Math.min(src.width, src.height); // cover the circle
      img.setScale(s).setOrigin(0.5, 0.34); // bias toward the sky/landmark up top
      const maskG = this.make.graphics({ x: 0, y: 0 }, false);
      maskG.fillStyle(0xffffff).fillCircle(node.x, node.y, NODE_R);
      img.setMask(maskG.createGeometryMask());
      return img;
    }
    // No stage art yet — a tinted disc previews the realm's colour.
    const g = this.add.graphics().setDepth(8);
    g.fillStyle(0x1b1e30, 1).fillCircle(node.x, node.y, NODE_R);
    g.fillStyle(tint, 0.55).fillCircle(node.x, node.y, NODE_R);
    g.fillStyle(0x0b0a14, 0.25).fillCircle(node.x, node.y - NODE_R * 0.2, NODE_R * 0.7);
    return g;
  }

  /** Stage-backdrop texture key for a node's level, if loaded (the in-battle
   *  plate doubles as the level preview). Olympus stop 1 uses the legacy key. */
  private stageBgKey(node: MapNode): string | null {
    if (!node.levelId) return null;
    const perStage = `world_background_${node.levelId}`;
    if (this.textures.exists(perStage)) return perStage;
    if (node.regionId === 'olympus' && this.textures.exists('world_background')) return 'world_background';
    return null;
  }

  /** The tier ring: a metal band whose colour climbs with the best difficulty
   *  cleared (bronze→silver→gold→radiant); unplayed-but-unlocked is a thin slate
   *  ring; locked stops get no ring (the sealed sigil reads instead). */
  private drawRing(g: Phaser.GameObjects.Graphics, node: MapNode, unlocked: boolean, bestTier: number): void {
    g.clear();
    if (!unlocked) return;
    const t = TIER_RING[Phaser.Math.Clamp(bestTier, 0, 4)];
    if (t.glow) {
      g.lineStyle(13, t.color, 0.22).strokeCircle(node.x, node.y, NODE_R + 7);
    }
    g.lineStyle(bestTier === 0 ? 4 : 7, t.color, bestTier === 0 ? 0.7 : 1);
    g.strokeCircle(node.x, node.y, NODE_R + 5);
  }

  // ---- floating Heaven finale ----------------------------------------------

  private buildHeaven(): void {
    this.heavenNode = WORLD_MAP.nodes.find((n) => mapRegion(n.regionId)?.realm === 'heaven');
    // A screen-space veil that darkens the receding mortal world during the
    // ascension (above the map + nodes at depth ≤8, below Heaven at depth 11).
    this.veil = this.add
      .rectangle(0, 0, DESIGN.width, DESIGN.height, 0x05040c)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(9)
      .setAlpha(0)
      .setVisible(false);
    if (!this.heavenNode) return;
    this.heaven = new HeavenSetpiece(this, this.heavenNode.x, this.heavenNode.y);
    // Hidden until the finale unlocks; the ascension fades it in. (A pending
    // reveal also starts hidden so the cinematic plays it in.)
    const shown = progress.isUnlocked(this.heavenNode.id) && progress.peekPendingReveal() !== this.heavenNode.id;
    this.heaven.layer.setVisible(shown).setAlpha(shown ? 1 : 0);
  }

  /** The ascension: lock input, lift the camera past the top of the mortal map
   *  into the sky, veil the world receding below, and float Heaven in. */
  private playHeavenAscension(onArrive?: () => void): void {
    if (this.cinematic || !this.heaven) {
      onArrive?.();
      return;
    }
    this.cinematic = true;
    this.camCtl.enabled = false;
    const cam = this.cameras.main;
    cam.setBounds(0, -1400, MAP.width, MAP.height + 1400);

    this.veil.setAlpha(0).setVisible(true);
    this.tweens.add({ targets: this.veil, alpha: 0.6, duration: 1000, ease: 'Quad.easeOut' });

    const h = this.heaven;
    h.layer.setVisible(true).setAlpha(h.layer.alpha < 1 ? 0 : 1);
    h.layer.setScale(0.9);
    this.tweens.add({ targets: h.layer, alpha: 1, duration: 900, ease: 'Quad.easeOut' });
    this.tweens.add({ targets: h.layer, scale: 1, duration: 1300, ease: 'Sine.easeOut' });

    sfx.play('victory'); // a triumphant stinger for the ascent
    const z = 0.6;
    this.tweens.add({
      targets: cam,
      zoom: z,
      scrollX: h.focusX - DESIGN.width / 2 / z,
      scrollY: h.focusY - DESIGN.height / 2 / z,
      duration: 1700,
      ease: 'Sine.easeInOut',
      onComplete: () => onArrive?.(),
    });
  }

  /** Descend back to the mortal atlas (closing the finale panel). */
  private exitCinematic(): void {
    if (!this.cinematic) return;
    this.cinematic = false;
    const cam = this.cameras.main;
    this.tweens.add({ targets: this.veil, alpha: 0, duration: 600, onComplete: () => this.veil.setVisible(false) });
    this.tweens.add({
      targets: cam,
      zoom: this.fitZoom(),
      scrollX: 0,
      scrollY: 0,
      duration: 1000,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        cam.setBounds(0, 0, MAP.width, MAP.height);
        this.camCtl.enabled = true;
      },
    });
  }

  private titleTaps = 0;
  private lastTitleTap = 0;

  /** Count rapid taps on the map title; 7 within ~1.4s each ascends to Heaven
   *  (a hidden, mobile-friendly way to see the finale set-piece). */
  private onTitleTap(): void {
    if (this.cinematic || !this.heaven) return;
    const now = this.time.now;
    this.titleTaps = now - this.lastTitleTap > 1400 ? 1 : this.titleTaps + 1;
    this.lastTitleTap = now;
    if (this.titleTaps >= 7) {
      this.titleTaps = 0;
      this.playHeavenAscension(() => this.openPanel(this.heavenNode!));
    }
  }

  /** DEV-only: press H to preview the Heaven ascension without clearing the
   *  whole campaign. Stripped from production builds. */
  private setupDevKeys(): void {
    const dev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV;
    if (!dev || !this.heavenNode) return;
    this.input.keyboard?.on('keydown-H', () => {
      if (this.cinematic) return;
      this.playHeavenAscension(() => this.openPanel(this.heavenNode!));
    });
  }

  // ---- reveal animation ----------------------------------------------------

  /** When a victory unlocked a new stop, lift its fog, ink in its trail, and pop
   *  the medallion — once. Plays after a short beat so the map settles first. */
  private playPendingReveal(): void {
    const id = progress.peekPendingReveal();
    if (!id) return;
    // Reaching the finale is its own set-piece, not a flat node pop.
    if (this.heavenNode && id === this.heavenNode.id) {
      this.time.delayedCall(500, () =>
        this.playHeavenAscension(() => {
          this.openPanel(this.heavenNode!);
          progress.consumePendingReveal();
        }),
      );
      return;
    }
    const node = mapNode(id);
    const view = this.nodeViews.find((v) => v.node.id === id);
    if (!node || !view) {
      progress.consumePendingReveal();
      return;
    }

    // A dark soft patch sitting over the still-fogged new region; it fades to
    // reveal the painted terrain (the fog hole is erased underneath up front).
    this.eraseFogHole(node.x, node.y);
    const patch = this.add
      .image(node.x, node.y, 'fog_brush')
      .setDepth(7)
      .setTint(0x07060f)
      .setAlpha(0.95);
    this.uiCam?.ignore(patch); // world-space, created after the camera split

    const cam = this.cameras.main;
    this.time.delayedCall(450, () => {
      // 1) Pan to the new stop, then dissolve the fog patch.
      this.tweens.add({ targets: cam, scrollX: node.x - DESIGN.width / 2 / cam.zoom, scrollY: node.y - DESIGN.height / 2 / cam.zoom, duration: 700, ease: 'Sine.easeInOut' });
      this.tweens.add({
        targets: patch,
        alpha: 0,
        duration: 800,
        delay: 300,
        ease: 'Quad.easeOut',
        onComplete: () => {
          patch.destroy();
          // 2) Ink the trail from the prereq toward the new stop.
          this.animateRoute(node, () => {
            // 3) Pop the medallion + ring in.
            sfx.play('upgrade');
            view.medallion.setActive(true);
            this.tweens.add({ targets: [view.medallion, view.ring], alpha: 1, duration: 350, ease: 'Back.easeOut' });
            progress.consumePendingReveal();
          });
        },
      });
    });
  }

  /** Draw the pending leg's dots one-by-one, then call `done`. */
  private animateRoute(node: MapNode, done: () => void): void {
    const from = node.prereq ? mapNode(node.prereq) : undefined;
    if (!from) {
      done();
      return;
    }
    const pts = this.routePoints(from, node);
    let i = 0;
    this.time.addEvent({
      repeat: pts.length - 1,
      delay: 45,
      callback: () => {
        i++;
        this.drawDots(pts, i);
        if (i >= pts.length) done();
      },
    });
  }

  // ---- interaction ---------------------------------------------------------

  private handleTap(worldX: number, worldY: number): void {
    // A tap landing on the open panel is for ITS buttons (tier chips / Embark /
    // close), handled by their own interactive zones — don't treat it as a map
    // tap (which would close the panel out from under the press).
    if (this.panel.visible && this.panelBounds) {
      const p = this.input.activePointer;
      const b = this.panelBounds;
      if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) return;
    }
    let hit: MapNode | null = null;
    let bestD2 = (NODE_R + 16) * (NODE_R + 16);
    for (const v of this.nodeViews) {
      const d2 = (worldX - v.node.x) ** 2 + (worldY - v.node.y) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        hit = v.node;
      }
    }
    if (hit) {
      sfx.play('click');
      // Tapping the unlocked finale ascends to Heaven before showing its panel.
      if (this.heavenNode && hit.id === this.heavenNode.id && progress.isUnlocked(hit.id)) {
        this.playHeavenAscension(() => this.openPanel(hit!));
        return;
      }
      this.openPanel(hit);
    } else {
      this.closePanel();
    }
  }

  // ---- node info / embark panel (screen-space) -----------------------------

  private buildUi(): void {
    // Header band + back-to-menu, fixed to the screen.
    this.add.rectangle(0, 0, DESIGN.width, 64, 0x07060f, 0.55).setOrigin(0, 0).setScrollFactor(0).setDepth(18);
    this.add
      .text(DESIGN.width / 2, 32, 'THE PILGRIMAGE', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: COLORS.accent,
        letterSpacing: 2,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(19);
    // Easter egg: 7 quick taps on the title ascends to Heaven — the mobile-
    // friendly stand-in for the DEV-only `H` hotkey (works in production too).
    // A scene-level pointer check (screen space, DESIGN coords) is reliable on
    // every device, unlike a scrollFactor-0 interactive zone's hit area.
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (!this.cinematic && p.y <= 60 && Math.abs(p.x - DESIGN.width / 2) < 170) this.onTitleTap();
    });
    this.makeChip(56, 32, '‹ Menu', () => {
      sfx.play('click');
      this.scene.start('menu');
    });

    this.panel = this.add.container(0, 0).setScrollFactor(0).setDepth(20).setVisible(false);
  }

  private openPanel(node: MapNode): void {
    this.panel.removeAll(true);
    this.panel.setVisible(true);

    const unlocked = progress.isUnlocked(node.id);
    const level = node.levelId ? getLevel(node.levelId) : undefined;
    const region = mapRegion(node.regionId);
    const W = 660;
    const H = 250;
    const cx = DESIGN.width / 2;
    const cy = DESIGN.height - H / 2 - 20;
    this.panelBounds = { x: cx - W / 2, y: cy - H / 2, w: W, h: H };

    const bg = this.add.graphics();
    bg.fillStyle(0x0f0d18, 0.96).fillRoundedRect(cx - W / 2, cy - H / 2, W, H, 16);
    bg.lineStyle(2, 0x7a5a2e, 0.9).strokeRoundedRect(cx - W / 2, cy - H / 2, W, H, 16);
    this.panel.add(bg);

    const realmName = region?.name ?? '';
    this.panel.add(this.text(cx, cy - H / 2 + 24, `${realmName} · ${node.name}`, 22, COLORS.accent, true));
    this.panel.add(this.text(cx, cy - H / 2 + 56, node.flavor, 16, '#aab0d8'));

    // A close affordance.
    const close = this.add.text(cx + W / 2 - 26, cy - H / 2 + 18, '✕', { fontFamily: 'system-ui, sans-serif', fontSize: '22px', color: '#aab0d8' }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    close.on('pointerup', () => this.closePanel());
    this.panel.add(close);

    if (!unlocked) {
      this.panel.add(this.text(cx, cy + 24, '🔒  Sealed', 22, '#c7ccd6', true));
      this.panel.add(this.text(cx, cy + 58, 'Clear the path to reach this stop.', 16, '#7e84ad'));
      return;
    }
    if (!level) {
      this.panel.add(this.text(cx, cy + 24, '⚒  Coming soon', 22, '#c7ccd6', true));
      this.panel.add(this.text(cx, cy + 58, 'This realm awaits — its trial is still being forged.', 16, '#7e84ad'));
      return;
    }

    // Built & unlocked → difficulty chips + Embark.
    const best = progress.getBestTier(node.levelId!);
    this.selectedTier = Phaser.Math.Clamp(best > 0 ? best : 1, 1, level.difficulties.length || 1);
    this.buildTierChips(cx, cy + 6, level.difficulties, best);
    this.buildEmbark(cx, cy + H / 2 - 34, node.levelId!);
  }

  private buildTierChips(cx: number, y: number, tiers: { tier: number; name: string }[], best: number): void {
    const redraws: (() => void)[] = [];
    const w = 138;
    const h = 44;
    const gap = 10;
    const total = tiers.length * w + (tiers.length - 1) * gap;
    let x = cx - total / 2 + w / 2;
    for (const t of tiers) {
      const bx = x;
      const g = this.add.graphics();
      const cleared = best >= t.tier;
      const label = this.add
        .text(bx, y, `${t.tier} · ${t.name}${cleared ? ' ✓' : ''}`, { fontFamily: 'system-ui, sans-serif', fontSize: '16px', fontStyle: 'bold', color: COLORS.text })
        .setOrigin(0.5);
      const redraw = (): void => {
        const sel = this.selectedTier === t.tier;
        g.clear();
        g.fillStyle(sel ? 0x5ac0a0 : 0x2c3052, 1);
        g.lineStyle(2, sel ? 0x8ee0c4 : 0x474d77, 1);
        g.fillRoundedRect(bx - w / 2, y - h / 2, w, h, 10);
        g.strokeRoundedRect(bx - w / 2, y - h / 2, w, h, 10);
        label.setColor(sel ? '#10261e' : '#c9cdf0');
      };
      redraws.push(redraw);
      redraw();
      const zone = this.add.zone(bx, y, w, h).setInteractive({ useHandCursor: true });
      zone.on('pointerup', () => {
        sfx.play('click');
        this.selectedTier = t.tier;
        for (const r of redraws) r();
      });
      this.panel.add([g, label, zone]);
      x += w + gap;
    }
  }

  private buildEmbark(cx: number, y: number, levelId: string): void {
    const w = 260;
    const h = 56;
    const g = this.add.graphics();
    g.fillStyle(0xe0b04a, 1).fillRoundedRect(cx - w / 2, y - h / 2, w, h, 12);
    const label = this.add.text(cx, y, 'EMBARK  ▶', { fontFamily: 'system-ui, sans-serif', fontSize: '26px', fontStyle: 'bold', color: '#10131f' }).setOrigin(0.5);
    const zone = this.add.zone(cx, y, w, h).setInteractive({ useHandCursor: true });
    zone.on('pointerup', () => {
      sfx.play('click');
      this.scene.start('battle', { levelId, difficultyTier: this.selectedTier });
    });
    this.panel.add([g, label, zone]);
  }

  private closePanel(): void {
    this.panel.setVisible(false);
    this.panel.removeAll(true);
    this.panelBounds = undefined;
    // Closing the finale panel descends back to the mortal atlas.
    if (this.cinematic) this.exitCinematic();
  }

  // ---- helpers -------------------------------------------------------------

  private text(x: number, y: number, s: string, size: number, color: string, bold = false): Phaser.GameObjects.Text {
    return this.add
      .text(x, y, s, { fontFamily: 'system-ui, sans-serif', fontSize: `${size}px`, fontStyle: bold ? 'bold' : 'normal', color, align: 'center', wordWrap: { width: 600 } })
      .setOrigin(0.5);
  }

  private makeChip(x: number, y: number, label: string, onClick: () => void): void {
    const t = this.add
      .text(x, y, label, { fontFamily: 'system-ui, sans-serif', fontSize: '20px', color: COLORS.text, backgroundColor: '#2b3052', padding: { x: 12, y: 6 } })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(19)
      .setInteractive({ useHandCursor: true });
    t.on('pointerup', onClick);
  }

  // ---- pulse the "play me" frontier nodes ----------------------------------

  update(): void {
    this.heaven?.tick(this.time.now);
    const k = 1 + 0.06 * Math.sin(this.time.now / 260);
    for (const v of this.nodeViews) {
      if (!v.pulse) continue;
      v.ring.setScale(k);
      // The ring graphics is drawn around the node's world point, so scale it
      // about that point rather than the origin.
      v.ring.setPosition(v.node.x * (1 - k), v.node.y * (1 - k));
    }
  }
}
